import { useMemo } from 'react';
import { useVacationDetailQuery, useGetVacationFile } from '@features/vacation/hooks';

import safeToBase64Image from '@shared/lib/safeToBase64Image';
import StatusBadge from '@shared/components/atoms/StatusBadge';
import LoadingOverlay from '@shared/components/atoms/LoadingOverlay/LoadingOverlay';

import MobileModalHeader from '@shared/components/atoms/MobileModalHeader';
import MobileVacationDiv from '@shared/components/organisms/MobileVacationDiv';
import styles from '@pages/vacation/components/mobile/MobileVacationRequetContent/MobileVacationRequestDetail.module.scss';
import clip from '@shared/assets/images/clip.svg';
import chevronRight from '@shared/assets/images/chevron-right_icon.svg';
import { renderApproverSign } from '@shared/components/atoms/RenderApproverSign';

export default function MobileVacationRequestDetail({ vacationIdx, vacationStatus, onClose }) {
  const { mutate: downloadFile, isPending: isDownloadingFile } = useGetVacationFile();

  const {
    data: vacationData,
    isLoading,
    isFetching,
    isError,
  } = useVacationDetailQuery(vacationIdx);

  const { writerBase64, signerBase64 } = useMemo(() => {
    if (!vacationData) {
      return {
        writerBase64: null,
        signerBase64: null,
      };
    }
    return {
      writerBase64: vacationData.writer_sign ? safeToBase64Image(vacationData.writer_sign) : null,

      signerBase64: vacationData.signer_sign ? safeToBase64Image(vacationData.signer_sign) : null,
    };
  }, [vacationData]);

  function handleFileClick(fileIdx, fileName, fileURL) {
    downloadFile({ fileIdx, fileName, fileURL });
  }

  return (
    <>
      <div className={styles.page}>
        <MobileModalHeader title={'휴가상세'} onBack={onClose} />
        <div className={styles.container}>
          {isDownloadingFile && <LoadingOverlay message="파일 다운로드 중..." />}
          {!isLoading && vacationData && (
            <>
              <div className={styles.haederContainer}>
                <div className={styles.headerTop}>
                  <div className={styles.headerStatus}>
                    <StatusBadge state={vacationData.state} />
                  </div>
                  <div className={styles.headerVacationRange}>
                    {vacationData.start_date} ~ {vacationData.end_date}
                  </div>
                </div>
                <div className={styles.headerBottom}>
                  <div className={styles.sginContainer}>
                    <div className={styles.signTop}>기안자</div>
                    <div className={styles.signBottom}>
                      <img
                        src={writerBase64 || undefined}
                        width={54}
                        height={54}
                        alt="신청자 전자서명 "
                      />
                    </div>
                  </div>
                  <div className={styles.sginContainer}>
                    <div className={styles.signTop}>결재자</div>
                    <div className={styles.signBottom}>
                      {renderApproverSign(vacationData.state, signerBase64, styles)}
                    </div>
                  </div>
                </div>
              </div>
              <MobileVacationDiv title={'소속'} children={vacationData.writerInfo?.team} />
              <MobileVacationDiv title={'성명'} children={vacationData.writerInfo?.name} />
              <MobileVacationDiv
                title={'신청내역'}
                children={
                  (vacationData.vacation_type?.name || '-') +
                  (vacationData.cc_type?.name ? ' - ' + vacationData.cc_type.name : '')
                }
              />
              <MobileVacationDiv
                title={'휴가기간'}
                children={
                  vacationData.start_date +
                  ' ~ ' +
                  vacationData.end_date +
                  ' (' +
                  vacationData.period +
                  '일)'
                }
              />
              <MobileVacationDiv title={'신청사유'} children={vacationData.reason} />

              <MobileVacationDiv title="첨부파일" showDivider={false}>
                {Array.isArray(vacationData.fileList) && vacationData.fileList.length > 0 ? (
                  <div className={styles.fileList}>
                    {vacationData.fileList.map((file) => (
                      <div
                        key={file.idx}
                        className={styles.fileItem}
                        onClick={() =>
                          handleFileClick(file.idx, file.name, file.url || file.fileURL)
                        }
                      >
                        <img src={clip} className={styles.clip} />
                        {file.name}
                        <img src={chevronRight} className={styles.chevronRight} />
                      </div>
                    ))}
                  </div>
                ) : (
                  <span className={styles.emptyText}>첨부파일이 없습니다.</span>
                )}
              </MobileVacationDiv>
              <MobileVacationDiv
                title={'참조자'}
                children={
                  Array.isArray(vacationData.recipientInfos) &&
                  0 < vacationData.recipientInfos.length ? (
                    vacationData.recipientInfos.map((r) => r.name).join(', ')
                  ) : (
                    <span className={styles.emptyText}>참조자가 없습니다.</span>
                  )
                }
              />
              {vacationData.reject_reason && (
                <MobileVacationDiv title={'코멘트'} children={vacationData.reject_reason} />
              )}
              <MobileVacationDiv
                title={'총 휴가 잔여분'}
                children={vacationStatus.remaining.total + '일'}
              />
            </>
          )}
        </div>
      </div>
    </>
  );
}
